import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Youtube from '@tiptap/extension-youtube';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Iframe } from '@/components/editor/IframeExtension';
import { useEffect, useCallback, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link as LinkIcon,
  ImageIcon,
  Youtube as YoutubeIcon,
  Undo,
  Redo,
  Quote,
  Minus,
  Upload,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { buildSafeStoragePath, validateUpload, safeContentType } from '@/lib/sanitizeFilename';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

function ToolbarButton({
  onClick,
  active,
  children,
  title,
}: {
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('h-8 w-8', active && 'bg-accent text-accent-foreground')}
      onClick={onClick}
      title={title}
    >
      {children}
    </Button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary underline' } }),
      Youtube.extend({
        parseHTML() {
          return [
            { tag: 'div[data-youtube-video] iframe' },
            {
              tag: 'iframe[src*="youtube.com/embed"]',
              getAttrs: (el) => ({ src: (el as HTMLElement).getAttribute('src') }),
            },
            {
              tag: 'iframe[src*="youtube-nocookie.com/embed"]',
              getAttrs: (el) => ({ src: (el as HTMLElement).getAttribute('src') }),
            },
            {
              tag: 'iframe[src*="youtu.be"]',
              getAttrs: (el) => ({ src: (el as HTMLElement).getAttribute('src') }),
            },
          ];
        },
      }).configure({ width: 640, height: 360, HTMLAttributes: { class: 'mx-auto rounded-lg' } }),
      Iframe,
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
      Placeholder.configure({ placeholder: placeholder ?? 'Escribe el contenido...' }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none min-h-[200px] p-3 focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const addLink = useCallback(() => {
    if (!editor || !linkUrl) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    setLinkUrl('');
  }, [editor, linkUrl]);

  const addImage = useCallback(() => {
    if (!editor || !imageUrl) return;
    editor.chain().focus().setImage({ src: imageUrl }).run();
    setImageUrl('');
  }, [editor, imageUrl]);

  const uploadImageFile = useCallback(
    async (file: File) => {
      if (!editor) return;
      // A14: solo imágenes, con límite de tamaño; bloquea SVG/HTML/JS.
      const check = validateUpload(file, { allow: /^image\// });
      if (!check.ok) { toast.error(check.error); setUploadingImage(false); if (imageFileInputRef.current) imageFileInputRef.current.value = ''; return; }

      setUploadingImage(true);
      try {
        const path = buildSafeStoragePath('uploads/editor', file.name);
        const { error } = await supabase.storage.from('content').upload(path, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: safeContentType(file),
        });
        if (error) throw error;
        const { data } = supabase.storage.from('content').getPublicUrl(path);
        editor.chain().focus().setImage({ src: data.publicUrl }).run();
        toast.success('Imagen subida');
      } catch (err: any) {
        toast.error('Error al subir imagen: ' + (err?.message ?? ''));
      } finally {
        setUploadingImage(false);
        if (imageFileInputRef.current) imageFileInputRef.current.value = '';
      }
    },
    [editor],
  );


  const addYoutube = useCallback(() => {
    if (!editor || !youtubeUrl) return;
    editor.chain().focus().setYoutubeVideo({ src: youtubeUrl }).run();
    setYoutubeUrl('');
  }, [editor, youtubeUrl]);

  if (!editor) return null;

  return (
    <div className="rounded-md border border-input bg-background">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Negrita">
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Cursiva">
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-6 bg-border mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Título H2">
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Título H3">
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-6 bg-border mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista">
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerada">
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Cita">
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador">
          <Minus className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-6 bg-border mx-1" />

        {/* Link popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className={cn('h-8 w-8', editor.isActive('link') && 'bg-accent text-accent-foreground')} title="Enlace">
              <LinkIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="start">
            <div className="flex gap-2">
              <Input placeholder="https://..." value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addLink()} className="text-sm" />
              <Button size="sm" onClick={addLink}>Insertar</Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Image popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" title="Imagen">
              <ImageIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3 space-y-3" align="start">
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Subir desde el dispositivo</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                disabled={uploadingImage}
                onClick={() => imageFileInputRef.current?.click()}
              >
                {uploadingImage ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Subiendo...</>
                ) : (
                  <><Upload className="mr-2 h-4 w-4" />Elegir imagen</>
                )}
              </Button>
              <input
                ref={imageFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadImageFile(f);
                }}
              />
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-popover px-2 text-muted-foreground">o pegar URL</span></div>
            </div>
            <div className="flex gap-2">
              <Input placeholder="https://..." value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addImage()} className="text-sm" />
              <Button size="sm" onClick={addImage}>Insertar</Button>
            </div>
          </PopoverContent>

        </Popover>

        {/* YouTube popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" title="Video YouTube">
              <YoutubeIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="start">
            <div className="flex gap-2">
              <Input placeholder="URL de YouTube..." value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addYoutube()} className="text-sm" />
              <Button size="sm" onClick={addYoutube}>Insertar</Button>
            </div>
          </PopoverContent>
        </Popover>

        <div className="w-px h-6 bg-border mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Deshacer">
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Rehacer">
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} />
    </div>
  );
}
